import React, { useRef } from 'react';
import styled from 'styled-components';
import DatePicker, { ReactDatePickerProps, registerLocale } from 'react-datepicker';
import ko from 'date-fns/locale/ko';
import getYear from 'date-fns/getYear';
import getMonth from 'date-fns/getMonth';
import 'react-datepicker/dist/react-datepicker.css';
import { ReactComponent as IconArrowButtonRightMin } from '@/assets/images/common/IconArrowsButtonRightMin.svg';

registerLocale('ko', ko);

const CustomDatePicker = (props: ReactDatePickerProps) => {
  const { ...pickerProps } = props;
  const calendar = useRef(null);
  const months = Array.from({ length: 12 }, () => '').map((i, idx) =>
    ('00' + (idx + 1)).slice(-2)
  );

  const getDayName = (date: Date) => {
    return date.toLocaleDateString('ko-KR', { weekday: 'long' }).substr(0, 1);
  };

  const createDate = (date: Date) => {
    return new Date(
      new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
    );
  };

  return (
    <Wrapper>
      <Picker
        {...pickerProps}
        ref={calendar}
        locale="ko"
        dayClassName={date =>
          getDayName(createDate(date)) === '토'
            ? 'saturday'
            : getDayName(createDate(date)) === '일'
            ? 'sunday'
            : 'black'
        }
        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
          <div className={'year-wrapper'}>
            <div
              className="btn_month btn_month-prev"
              onClick={decreaseMonth}
              style={{ display: 'flex', transform: 'rotate(180deg)' }}>
              <IconArrowButtonRightMin />
            </div>
            <div className="month-day">
              {getYear(date)}.{months[getMonth(date)]}
            </div>

            <div
              className="btn_month btn_month-next"
              onClick={increaseMonth}
              style={{ display: 'flex' }}>
              <IconArrowButtonRightMin />
            </div>
          </div>
        )}
        onChangeRaw={e => e.preventDefault()}
      />
    </Wrapper>
  );
};

export default CustomDatePicker;

const Wrapper = styled.div`
  display: flex;

  & .react-datepicker {
    padding: 24px;
    overflow: hidden;
    border-radius: 8px !important;
    background: ${({ theme }) => theme.DF_COLOR.WHITE};
    border: ${({ theme }) => `1px solid ${theme.DF_COLOR.GRAY100}`} !important;
  }

  & input {
    border: ${({ theme }) => `1px solid ${theme.DF_COLOR.GRAY100}`};
    border-radius: 4px;
    height: 42px;
    padding: 14px 16px;
  }

  & .year-wrapper {
    margin: 0 auto 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 150px;
  }

  & .month-day {
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI14SB};
    color: ${({ theme }) => theme.DF_COLOR.GRAY900};
  }

  & .react-datepicker__day-names {
    width: 100%;
    height: 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 22px;
    background: ${({ theme }) => theme.DF_COLOR.WHITE};
  }

  & .react-datepicker__month {
    padding-bottom: 25px;
    margin: 1.4rem;
  }

  & .react-datepicker__header {
    background: #fff;
    padding: 0;
    border-bottom: none;
  }

  & .react-datepicker-popper[data-placement^='bottom'],
  . react-datepicker__triangle::after {
    border-bottom-color: #fff;
  }

  & .react-datepicker__month-container {
    font-size: 17px;
  }

  & .react-datepicker__month {
    margin: 0;
  }

  & .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: 32px;
    margin: 0 6px;
    height: 32px;
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI14M};
    color: ${({ theme }) => theme.DF_COLOR.GRAY900};
  }

  & .react-datepicker__week {
    background: ${({ theme }) => theme.DF_COLOR.WHITE};
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  & .react-datepicker__day {
    border-radius: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  & .saturday {
    color: ${({ theme }) => theme.DF_COLOR.BLUE700};
  }

  & .sunday {
    color: ${({ theme }) => theme.DF_COLOR.RED700};
  }

  .react-datepicker__day--disabled,
  .react-datepicker__month-text--disabled,
  .react-datepicker__quarter-text--disabled,
  .react-datepicker__year-text--disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.DF_COLOR.GRAY200} !important;
  }

  .react-datepicker__month.react-datepicker__month-text,
  .react-datepicker__month.react-datepicker__quarter-text {
    padding: 5px 30px;
    font-size: 18px;
    font-weight: 600;
    width: 7rem;
  }

  .react-datepicker__day {
    &:hover {
      border-radius: 16px;
      background: ${({ theme }) => theme.DF_COLOR.PRIMARY700};
      color: ${({ theme }) => theme.DF_COLOR.WHITE};
    }
  }

  .react-datepicker__day--selected {
    background: ${({ theme }) => theme.DF_COLOR.PRIMARY700};
    color: ${({ theme }) => theme.DF_COLOR.WHITE};

    &:hover {
      border-radius: 16px;
      background: ${({ theme }) => theme.DF_COLOR.PRIMARY100};
      color: ${({ theme }) => theme.DF_COLOR.PRIMARY700};
    }
  }

  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range {
    border-radius: 16px;
    background: ${({ theme }) => theme.DF_COLOR.PRIMARY100} !important;
    color: ${({ theme }) => theme.DF_COLOR.PRIMARY700} !important;

    &:hover {
      border-radius: 16px;
      background: ${({ theme }) => theme.DF_COLOR.PRIMARY700};
      color: ${({ theme }) => theme.DF_COLOR.WHITE};
    }
  }

  .react-datepicker__month-text--keyboard-selected {
    border-radius: 16px;
    background: ${({ theme }) => theme.DF_COLOR.PRIMARY100};
    color: ${({ theme }) => theme.DF_COLOR.PRIMARY700};
  }

  .
    react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range, .react-datepicker__month-text--in-range, .react-datepicker__quarter-text--in-range, .react-datepicker__year-text--in-range),
  .react-datepicker__month-text--in-selecting-range:not(.react-datepicker__day--in-range, .react-datepicker__month-text--in-range, .react-datepicker__quarter-text--in-range, .react-datepicker__year-text--in-range),
  .react-datepicker__quarter-text--in-selecting-range:not(.react-datepicker__day--in-range, .react-datepicker__month-text--in-range, .react-datepicker__quarter-text--in-range, .react-datepicker__year-text--in-range),
  .react-datepicker__year-text--in-selecting-range:not(.react-datepicker__day--in-range, .react-datepicker__month-text--in-range, .react-datepicker__quarter-text--in-range, .react-datepicker__year-text--in-range) {
    border-radius: 16px;
    background: ${({ theme }) => theme.DF_COLOR.PRIMARY100};
    color: ${({ theme }) => theme.DF_COLOR.PRIMARY700};
  }
`;

const Picker = styled(DatePicker)`
  padding: 0 20px;
`;

const ArrowButton = styled.div`
  cursor: pointer;
`;
